import { AsyncPipe, DatePipe, NgStyle, NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
    MatCardModule,
    MatCardSubtitle,
    MatCardTitle,
} from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import {
    MatListModule,
    MatListOption,
    MatSelectionList,
} from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
    combineLatest,
    debounceTime,
    filter,
    map,
    Observable,
    Subject,
    switchMap,
    tap,
} from 'rxjs';
import { Chat, Thread } from 'src/app/models/ai-chat-models/chat.model';

@Component({
    selector: 'app-thread-list',
    standalone: true,
    imports: [
        MatListModule,
        MatSelectionList,
        MatListOption,
        DatePipe,
        AsyncPipe,
        MatCardModule,
        MatCardTitle,
        MatCardSubtitle,
        MatProgressSpinnerModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        NgClass,
    ],
    templateUrl: './thread-list.component.html',
    styleUrl: './thread-list.component.scss',
})
export class ThreadListComponent implements OnInit {
    @Input() userThreads$: Observable<Thread[]>;
    @Input() threadBuffering$: Observable<boolean>;
    @Input() currentChat$: Observable<Chat>;
    @Input() newThreadIncoming: Observable<boolean>;
    @Input() chatLoading$: Observable<boolean>;
    @Output() newThreadSelected: EventEmitter<Thread> =
        new EventEmitter<Thread>();
    @Output() threadsToDelete: EventEmitter<Thread[]> = new EventEmitter<
        Thread[]
    >();
    @Output() createNewThread: EventEmitter<void> = new EventEmitter<void>();

    private debounceThreadSelect$ = new Subject<Thread>();
    private _currentLoadedThreads: Thread[] = [];
    private _pendingThreadsToBeDeleted: Thread[] = [];
    private _currentThreadsBeingDeleted: Thread[] = [];
    private _selectedThread: Thread;
    private _isCreateNewThreadDisabled$: Observable<boolean>;

    constructor() {}

    ngOnInit(): void {
        this.userThreads$
            .pipe(
                tap((threads: Thread[]) => {
                    this._currentLoadedThreads = threads;
                    this._currentThreadsBeingDeleted =
                        this._currentThreadsBeingDeleted.filter(
                            (deletedThread: Thread) =>
                                threads.some(
                                    (loadedThread) =>
                                        loadedThread.threadId ===
                                        deletedThread.threadId,
                                ),
                        );
                }),
                switchMap((threads: Thread[]) =>
                    this.currentChat$.pipe(
                        filter((c: Chat) => c != null),
                        map((chat: Chat) =>
                            threads.find((t) => t.threadId === chat.threadId),
                        ),
                    ),
                ),
            )
            .subscribe((selected: Thread) => {
                this._selectedThread = selected;
            });

        this.debounceThreadSelect$.subscribe((thread: Thread) =>
            this.changeSelectedThread(thread),
        );

        this._isCreateNewThreadDisabled$ = combineLatest([
            this.threadBuffering$,
            this.currentChat$,
            this.chatLoading$,
        ]).pipe(
            map(
                ([threadBuffering, currentChat, chatLoading]: [
                    boolean,
                    Chat | null,
                    boolean,
                ]) =>
                    threadBuffering ||
                    (currentChat !== null &&
                        currentChat?.threadName === null) ||
                    (!currentChat && chatLoading),
            ),
        );
    }

    protected deleteThread(thread: Thread): void {
        if (
            !this._currentThreadsBeingDeleted.some(
                (current) => current.threadId === thread.threadId,
            )
        ) {
            this._currentThreadsBeingDeleted.push(thread);
            this.threadsToDelete.emit([thread]);
        }
    }

    protected selectThread(thread: Thread): void {
        if (thread != null) {
            this.changeSelectedThread(thread);
        }
    }

    get isCreateNewThreadDisabled$(): Observable<boolean> {
        return this._isCreateNewThreadDisabled$;
    }

    private changeSelectedThread(thread: Thread): void {
        this._selectedThread = thread;
        this.newThreadSelected.emit(thread);
    }

    protected isThreadBeingDeleted(thread: Thread): boolean {
        return (
            this._currentThreadsBeingDeleted
                .concat(this._pendingThreadsToBeDeleted)
                ?.some(
                    (threadBeingDeleted: Thread) =>
                        threadBeingDeleted.threadId == thread.threadId,
                ) ?? false
        );
    }

    get currentLoadedThreads(): Thread[] {
        return this._currentLoadedThreads;
    }

    get pendingThreadsBeingDeleted(): Thread[] {
        return this._currentThreadsBeingDeleted;
    }

    get selectedThread(): Thread {
        return this._selectedThread;
    }
}
